import { LeftOutlined } from "@ant-design/icons";
import {
  BotTemplates,
  TriggerCreate,
  TriggerForEditor,
} from "@dots-projects/types";
import { Alert, Button, Radio, Spin } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TriggerEditorFrontend,
  useTriggerEditorStateDispatch,
} from "../TriggerEditor/TriggerEditor";
import { getSupaClient } from "../../lib/supabase";
import { useBotTemplateData } from "./useDiscordBotTemplates";
import { getTriggerTemplates } from "./TriggerTemplates";

type Props = {
  workspace: string;
  setNewTrigger: React.Dispatch<React.SetStateAction<TriggerCreate | null>>;
  typeOfTrigger: string;
  backgroundPosition: string[];
  botTemplates: BotTemplates[];
};

/**
 * Component to display detailed information about every template
 */
export default function TemplateDetails({
  workspace,
  setNewTrigger,
  typeOfTrigger,
  backgroundPosition,
  botTemplates,
}: Props) {
  const supabase = getSupaClient();
  const navigate = useNavigate();
  const { onPersist } = useTriggerEditorStateDispatch();
  const { isLoading, isFetching, data, error } = useBotTemplateData(
    typeOfTrigger,
    workspace
  );
  const [selectedTab, setSelectedTab] = useState("1");

  if (error) {
    navigate("/automation/new?type=templates");
  }

  if (!data) {
    return (
      <>
        <div className="flex justify-center mt-4">
          <Alert
            showIcon
            message="Failed to get this bot, please contact us"
            description="Below you will find the bots available at the moment."
            className="w-10/12"
            closable
          />
        </div>
        <p className="ml-5 text-lg font-semibold mt-5">Bots</p>
        <div className="flex gap-5 flex-wrap mt-5 mx-5 mb-4">
          {getTriggerTemplates(
            navigate,
            0,
            botTemplates.length,
            backgroundPosition,
            botTemplates
          )}
        </div>
      </>
    );
  }

  const { name, details_html, gif_url, trigger } = data;
  const triggerToRender = { ...trigger, isDraft: false };

  return (
    <Spin spinning={isLoading || isFetching}>
      <div className="h-full mx-4 mb-4">
        <p className="mt-6 text-lg mb-8 font-medium">{name}</p>
        <div className="flex gap-10">
          <div className="flex flex-col" style={{ flexBasis: "55%" }}>
            <div className="flex justify-center items-center">
              <Radio.Group
                onChange={(e) => {
                  setSelectedTab(e.target.value);
                }}
                value={selectedTab}
                style={{ marginBottom: 8 }}
              >
                <Radio.Button value="1">How it looks in Discord</Radio.Button>
                <Radio.Button value="2">Editor view</Radio.Button>
              </Radio.Group>
            </div>
            <div style={{ height: "400px" }}>
              {selectedTab === "1" ? (
                <img
                  src={gif_url}
                  alt="Discord demo gif"
                  className="h-full mx-auto rounded-xl"
                />
              ) : (
                <TriggerEditorFrontend
                  initialTrigger={triggerToRender as TriggerForEditor}
                  onPersist={onPersist}
                  isLoadingError={false}
                  initialValidationErrors={[]}
                  initialLiveTriggerId=""
                  error={null}
                  removeControls={true}
                  resizeMiniMap={true}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col pr-2" style={{ flexBasis: "45%" }}>
            <Button
              className="w-52 ml-auto mb-10"
              onClick={() => {
                navigate("/automation/new?type=templates");
              }}
            >
              <LeftOutlined /> Back to templates
            </Button>
            <div
              dangerouslySetInnerHTML={{ __html: details_html }}
              className="text-justify"
            />
            <Button
              type="primary"
              className="mx-auto mt-5 w-40"
              onClick={() => {
                setNewTrigger(trigger);
              }}
              size="large"
            >
              Pick this one!
            </Button>
          </div>
        </div>
      </div>
    </Spin>
  );
}
